/**
 * Shortest plausible country-code + national number. Gozem's markets (Togo,
 * Benin, Gabon, …) are all 11 digits international, so 8 is comfortably
 * permissive while still rejecting fragments — notably a masked card tail like
 * "4242", which must never be mistaken for a phone number.
 */
const MIN_E164_DIGITS = 8;

/**
 * Normalize a raw phone value to E.164 (`+<digits>`), or `undefined` when there
 * is no usable phone to send.
 *
 * ## Why this exists
 *
 * Three call sites independently wrote `` `+${raw}` ``, and each carried the same
 * defect: when `raw` was empty the template produced the bare string `"+"`, a
 * country-code prefix with no number. That value is truthy, so the
 * `...(phone ? { beneficiary_phone_number: phone } : {})` omit-guards in the API
 * layer let it through. The backend then resolved a Gozem Money user by phone
 * `"+"`, found none, and answered error code "1032" — surfaced to the user as a
 * generic "Une erreur est survenue".
 *
 * The rule this encodes: **a field we cannot resolve is omitted, never sent as a
 * sentinel.** Returning `undefined` (not `''`, not `'+'`) is what makes the
 * caller's spread omit the key.
 *
 * Accepts `unknown` because token properties are typed `Record<string, unknown>`
 * and the backend sends `int_phone` as a number on some processors.
 */
export function toE164(raw: unknown): string | undefined {
  if (raw === null || raw === undefined) {
    return undefined;
  }

  // Keep only the digits: strips the leading '+', spaces, dashes and parentheses,
  // so a value that is nothing but punctuation collapses to '' and is rejected.
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length < MIN_E164_DIGITS) {
    return undefined;
  }

  return `+${digits}`;
}

/**
 * True when `value` is a usable E.164 phone number.
 *
 * Used by the API layer's omit-guards, which must reject prefix-only values that
 * a plain truthiness check happily forwards.
 */
export function isUsablePhoneNumber(value: string | undefined | null): boolean {
  return toE164(value) !== undefined;
}
